import React, { useState } from 'react';
import { createAssessmentRetest } from '../../api/assessment';
import ReportIcon from './ReportIcons';

const validLearningUrl = (entry) => {
  const value = typeof entry?.homepage_url === 'string'
    ? entry.homepage_url.trim()
    : '';
  return entry?.available && /^https?:\/\//i.test(value) ? value : '';
};

export default function RetestLauncher({
  assessmentId,
  journey,
  reportActions,
  learningEntry,
  improvements = [],
  onRetestReady,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const state = journey?.state || 'open';
  const learningUrl = validLearningUrl(learningEntry);
  const learningPathAllowed = reportActions?.learning_allowed !== false;
  const showLearningAction = Boolean(
    learningUrl && learningPathAllowed,
  );
  const canStart = journey?.can_start_retest !== false;
  const showDirectRetestAction = canStart && reportActions?.direct_retest_allowed !== false;
  const assessmentOnlyOpen = (
    state === 'open' && !learningPathAllowed && showDirectRetestAction
  );

  if (!assessmentId || assessmentId === 'preview' || state === 'closed') return null;

  if (state === 'retest_in_progress') {
    return (
      <section className="v5r-retest" aria-labelledby="v5r-retest-title">
        <div className="v5r-retest-head">
          <div>
            <div className="v5r-analysis-eyebrow">下一阶段测评</div>
            <h2 id="v5r-retest-title">复测尚未完成</h2>
            <p>继续完成本轮作答后，报告会展示初测与复测的分数变化。</p>
          </div>
        </div>
        <div className="v5r-retest-action">
          <div><strong>已为你保留当前进度</strong></div>
          <button
            type="button"
            onClick={() => onRetestReady?.(journey?.retest_assessment_id)}
          >
            继续完成复测
          </button>
        </div>
      </section>
    );
  }

  const startRetest = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const { data } = await createAssessmentRetest(
        journey?.baseline_assessment_id || assessmentId,
      );
      onRetestReady?.(data.id);
    } catch (requestError) {
      const response = requestError?.response;
      if (response?.status === 409 && response.data?.code === 'retest_exists') {
        onRetestReady?.(response.data.assessment_id);
        return;
      }
      setError(response?.data?.detail || '复测暂时无法创建，请稍后重试。');
    } finally {
      setSubmitting(false);
    }
  };

  const suggestions = improvements
    .map((item) => (typeof item?.title === 'string' ? item.title.trim() : ''))
    .filter(Boolean)
    .slice(0, 3);

  return (
    <section className="v5r-retest" aria-labelledby="v5r-retest-title">
      <div className="v5r-retest-head">
        <div>
          <div className="v5r-analysis-eyebrow">下一步建议</div>
          <h2 id="v5r-retest-title">
            {state === 'learning_completed'
              ? '学习任务已完成，现在检验你的变化'
              : assessmentOnlyOpen
                ? '开始下一阶段测评'
                : '先学习，再验证你的提升'}
          </h2>
          <p>
            {state === 'learning_completed'
              ? '完成复测后，你将看到初测与复测的完整分数变化。'
              : assessmentOnlyOpen
                ? '完成下一阶段测评后，你将看到前后两次的完整分数变化。'
                : '建议先围绕报告中的提升方向完成学习任务，再回来复测，让结果更有参考价值。'}
          </p>
          {state === 'open' && !assessmentOnlyOpen && suggestions.length > 0 && (
            <ul aria-label="建议优先学习的方向">
              {suggestions.map((item) => <li key={item}>{item}</li>)}
            </ul>
          )}
        </div>
        <div className="v5r-retest-icon" aria-hidden="true">
          <ReportIcon name={assessmentOnlyOpen ? 'bar-chart' : 'book-open'} size={21} />
        </div>
      </div>

      <div className="v5r-retest-action">
        <div>
          <strong>
            {state === 'learning_completed'
              ? '进入本轮复测'
              : assessmentOnlyOpen
                ? '准备好后，开始下一阶段测评'
                : '学习完成后，回到这份报告继续'}
          </strong>
          {state === 'open' && showLearningAction && showDirectRetestAction && (
            <span>如果暂时不学习，也可以开始下一阶段测评。</span>
          )}
        </div>
        <div className="v5r-next-actions">
          {showLearningAction && (state === 'open' || learningEntry?.task_bound) && (
            <a className="v5r-learning-link" href={learningUrl}>
              <ReportIcon name="book-open" size={16} />
              {state === 'open' ? '先去学习，再回来复测' : '返回本次学习'}
            </a>
          )}
          {canStart && (state !== 'open' || showDirectRetestAction) && (
            <button
              className={state === 'open' && showLearningAction ? 'secondary' : ''}
              type="button"
              onClick={startRetest}
              disabled={submitting}
            >
              {submitting
                ? '正在创建…'
                : state === 'learning_completed'
                  ? '开始复测'
                  : '暂不学习，开始下一阶段测评'}
            </button>
          )}
        </div>
      </div>
      {error && <div className="v5r-retest-error" role="alert">{error}</div>}
    </section>
  );
}
